// src/components/institution/InstitutionSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  School, 
  User,
  Users, 
  Calendar, 
  Settings, 
  Shield, 
  Globe, 
  Layout, 
  Code, 
  AlertCircle 
} from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { SEOSection } from '../settings/shared/SEOSection';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { IntegrationsSection } from '../settings/shared/IntegrationsSection';
import { InteractionsSection } from '../settings/shared/InteractionsSection';
import { AdvancedSection } from '../settings/shared/AdvancedSection';
import { LayoutDisplaySection } from '../settings/shared/LayoutSection';
import { institutionService } from '../../services/institutionService';
import { firestoreService } from '../../services/firestoreService';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';

import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { ViewSettings } from '../settings/shared/ViewSettings';
import { PermissionsSettings } from '../settings/shared/PermissionsSettings';

export const InstitutionSettings = ({ book, bookId, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const [errors, setErrors] = useState({});

  // Keep your existing formData structure - NO CHANGES
  const [formData, setFormData] = useState({
    // Basic institution info
    botName: '',
    description: '',
    institutionType: 'school',
    tagline: '',
    establishedYear: '',

    // Header settings
    headerColor: '',
    headerTextColor: '',
    headerBackgroundUrl: '',
    headerOverlay: 'rgba(0, 0, 0, 0.3)',
    headerOverlayOpacity: 0.3,
    
    // ACCESS LEVEL - THIS WAS MISSING!
    accessLevel: 'public',
    requireAuth: false,
    allowIndexing: true,
    
    // SEO
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    ogImage: '',
    customDomain: '',
    customPath: '',
    enableStructuredData: true,
    
    // Styling
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    textColor: '',
    fontFamily: '',
    borderRadius: '',
    
    // Layout
    layoutStyle: '',
    enableParallax: true,
    enableAnimations: true,
    
    // Interactions
    enableComments: true,
    enableLikes: true,
    enableSharing: true,
    enableDownloads: false,
    
    // Advanced
    customCSS: '',
    customJS: '',
    analyticsId: '',
    
    // Institution-specific settings (flattened for easier form handling)
    academicYear: '',
    gradeRange: '',
    admissionProcess: '',
    enableInternalCommunications: true,
    enableResourceManagement: true,
    enableLearnerManagement: true,
    enablePerformanceTracking: true,
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,

    // Element Visibility
    showLogo: true,
    showTitle: true,
    showDescription: false,
    showSearch: true,
    showShareButton: true,
    showAccessLevel: true,
    showFilterControls: true,
    showGallerySelector: true,
    showViewModeToggle: true,
    showOwnerControls: true,

    // Styling
    headerHeight: 80,
    logoHeight: 40,
    position: 'sticky', // sticky|fixed|static
    layout: 'default', // default|centered|minimal|compact

    // Search Configuration
    searchPlaceholder: 'Search media...',
    searchPosition: 'center', // center|right|left|dropdown
    enableLiveSearch: true,
    showSearchOnMobile: false,

    // User Profile
    userProfileVisibility: 'auto', // auto|always|authenticated|never
    showProfileForPublic: false,
    requireLoginToView: false,
    
    sectionVisibility: {
      programs: true,
      admissions: true,
      staff: true,
      facilities: true,
      events: true,
      news: true,
      gallery: true
    }
  });

  // Keep your existing tabs - NO CHANGES
  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: School },
    { id: 'layout', label: 'Layout & Display', icon: Layout },
    { id: 'header', label: 'Header', icon: User},
    { id: 'design', label: 'Design & Branding', icon: Settings },
    { id: 'view', label: 'View Settings', icon: Globe },
    { id: 'permissions', label: 'Access Control', icon: Shield },
    { id: 'interactions', label: 'Interactions', icon: Users },
    { id: 'integrations', label: 'Integrations', icon: Settings },
    { id: 'seo', label: 'SEO & Sharing', icon: Globe },
    { id: 'privacy', label: 'Privacy & Access', icon: Shield },
    { id: 'advanced', label: 'Advanced', icon: Code }
  ];

  // Keep your existing useEffect - NO CHANGES
  useEffect(() => {
    if (book) {
      setFormData(prev => ({
        ...prev,
        // Basic info
        botName: book.botName || prev.botName,
        description: book.description || prev.description,
        institutionType: book.institutionType || prev.institutionType,
        tagline: book.tagline || prev.tagline,
        establishedYear: book.establishedYear || prev.establishedYear,

        // ACCESS LEVEL - CRITICAL!
        accessLevel: book.accessLevel || 'public',
        requireAuth: book.requireAuth === true,
        allowIndexing: book.allowIndexing !== false,
        
        // SEO
        seoTitle: book.seoTitle || prev.seoTitle,
        seoDescription: book.seoDescription || prev.seoDescription,
        seoKeywords: book.seoKeywords || prev.seoKeywords,
        ogImage: book.ogImage || prev.ogImage,
        customDomain: book.customDomain || prev.customDomain,
        customPath: book.customPath || prev.customPath,
        enableStructuredData: book.enableStructuredData !== false,
        
        // Styling
        primaryColor: book.primaryColor || book.styling?.primaryColor || prev.primaryColor,
        secondaryColor: book.secondaryColor || book.styling?.secondaryColor || prev.secondaryColor,
        backgroundColor: book.backgroundColor || book.styling?.backgroundColor || prev.backgroundColor,
        textColor: book.textColor || book.styling?.textColor || prev.textColor,
        headerColor: book.headerColor || book.styling?.headerColor || prev.headerColor,
        headerTextColor: book.headerTextColor || book.styling?.headerTextColor || prev.headerTextColor,
        fontFamily: book.fontFamily || book.styling?.fontFamily || prev.fontFamily,
        borderRadius: book.borderRadius || book.styling?.borderRadius || prev.borderRadius,

        headerBackgroundUrl: book.headerBackgroundUrl || prev.headerBackgroundUrl,
        headerOverlay: book.headerOverlay || prev.headerOverlay,
        headerOverlayOpacity: book.headerOverlayOpacity !== undefined ? book.headerOverlayOpacity : prev.headerOverlayOpacity,
        
        headerSettings: {
          // Element Visibility
          showLogo: book.institutionSettings?.headerSettings?.showLogo ?? true,
          showTitle: book.institutionSettings?.headerSettings?.showTitle ?? true,
          showDescription: book.institutionSettings?.headerSettings?.showDescription ?? false,
          showSearch: book.institutionSettings?.headerSettings?.showSearch ?? true,
          showShareButton: book.institutionSettings?.headerSettings?.showShareButton ?? true,
          showAccessLevel: book.institutionSettings?.headerSettings?.showAccessLevel ?? true,
          showFilterControls: book.institutionSettings?.headerSettings?.showFilterControls ?? true,
          showGallerySelector: book.institutionSettings?.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: book.institutionSettings?.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: book.institutionSettings?.headerSettings?.showOwnerControls ?? true,

          // Styling
          headerHeight: book.institutionSettings?.headerSettings?.headerHeight || 80,
          logoHeight: book.institutionSettings?.headerSettings?.logoHeight || 40,
          position: book.institutionSettings?.headerSettings?.position || 'sticky',
          layout: book.institutionSettings?.headerSettings?.layout || 'default',

          // Search Configuration
          searchPlaceholder: book.institutionSettings?.headerSettings?.searchPlaceholder || 'Search media...',
          searchPosition: book.institutionSettings?.headerSettings?.searchPosition || 'center',
          enableLiveSearch: book.institutionSettings?.headerSettings?.enableLiveSearch ?? true,
          showSearchOnMobile: book.institutionSettings?.headerSettings?.showSearchOnMobile ?? false,

          // User Profile
          userProfileVisibility: book.institutionSettings?.headerSettings?.userProfileVisibility || 'auto',
          showProfileForPublic: book.institutionSettings?.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: book.institutionSettings?.headerSettings?.requireLoginToView ?? false,

          ...book.institutionSettings?.headerSettings
        },
        
        // Layout
        layoutStyle: book.layoutStyle || prev.layoutStyle,
        enableParallax: book.enableParallax !== false,
        enableAnimations: book.enableAnimations !== false,
        
        // Interactions
        enableComments: book.enableComments !== false,
        enableLikes: book.enableLikes !== false,
        enableSharing: book.enableSharing !== false,
        enableDownloads: book.enableDownloads === true,
        
        // Advanced
        customCSS: book.customCSS || prev.customCSS,
        customJS: book.customJS || prev.customJS,
        analyticsId: book.analyticsId || prev.analyticsId,
        
        // Institution settings - flatten from nested structure
        academicYear: book.institutionSettings?.academicYear || prev.academicYear,
        gradeRange: book.institutionSettings?.gradeRange || prev.gradeRange,
        admissionProcess: book.institutionSettings?.admissionProcess || prev.admissionProcess,
        enableInternalCommunications: book.institutionSettings?.enableInternalCommunications !== false,
        enableResourceManagement: book.institutionSettings?.enableResourceManagement !== false,
        enableLearnerManagement: book.institutionSettings?.enableLearnerManagement !== false,
        enablePerformanceTracking: book.institutionSettings?.enablePerformanceTracking !== false,
        emailNotifications: book.institutionSettings?.emailNotifications !== false,
        smsNotifications: book.institutionSettings?.smsNotifications === true,
        pushNotifications: book.institutionSettings?.pushNotifications !== false,
        
        viewSettings: book.viewSettings || {
          enablePublicView: true,
          enableManagementView: true,
          publicViewLabel: 'Public View',
          managementViewLabel: 'Staff Portal',
          defaultView: 'public',
          requireAuth: true,
          allowedRoles: ['owner', 'admin', 'faculty', 'coordinator']
        },
        permissions: book.permissions || {},
        authorizedEmails: book.authorizedEmails || [],

        // Section visibility
        sectionVisibility: {
          ...prev.sectionVisibility,
          ...book.institutionSettings?.sectionVisibility
        }
      }));
    }
  }, [book]);

  // Keep your existing handleInputChange - NO CHANGES
  const handleInputChange = (path, value) => {
    console.log('Input change:', path, value); // Debug log
    
    setFormData(prev => {
      // Handle nested paths with dot notation
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        
        // Navigate to parent object
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          
          // Ensure nested object exists
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          
          current = current[key];
        }
        
        // Set the final value
        current[keys[keys.length - 1]] = value;
        
        return newData;
      }
      
      // Handle simple top-level properties
      return {
        ...prev,
        [path]: value
      };
    });
    
    // THIS IS THE KEY LINE THAT WAS MISSING
    setHasChanges(true);
  };

  // Keep your existing handleImageUpload - NO CHANGES
  const handleImageUpload = async (field, file) => {
    setUploadStates(prev => ({ ...prev, [field]: 'uploading' }));
    
    try {
      const uploadedUrl = await institutionService.uploadImage(file, book.id, field);
      handleInputChange(field, uploadedUrl);
      setUploadStates(prev => ({ ...prev, [field]: 'success' }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadStates(prev => ({ ...prev, [field]: 'error' }));
      setErrors(prev => ({ ...prev, [field]: 'Failed to upload image' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate format/structure, not required fields
    // Custom domain validation (only if provided)
    if (formData.customDomain && !isValidDomain(formData.customDomain)) {
      newErrors.customDomain = 'Please enter a valid domain (e.g., institution.yourdomain.com)';
    }
    
    // Custom path validation (only if provided)
    if (formData.customPath && !/^[a-zA-Z0-9-]+$/.test(formData.customPath)) {
      newErrors.customPath = 'Path can only contain letters, numbers, and hyphens';
    }
    
    // SEO validation (only if provided)
    if (formData.seoTitle && formData.seoTitle.length > 60) {
      newErrors.seoTitle = 'SEO title should be 60 characters or less';
    }
    
    if (formData.seoDescription && formData.seoDescription.length > 160) {
      newErrors.seoDescription = 'SEO description should be 160 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function for domain validation
  const isValidDomain = (domain) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };

  // Update handleSave to only update fields that have values
  const handleSave = async () => {
    try {
      setSaving(true);
      setErrors({});

      // Build complete update data with ALL fields
      const updateData = {
        // Basic info
        botName: formData.botName || '',
        description: formData.description || '',
        institutionType: formData.institutionType || 'school',
        tagline: formData.tagline || '',
        establishedYear: formData.establishedYear || '',
        
        // ACCESS LEVEL - CRITICAL FIELDS!
        accessLevel: formData.accessLevel || 'public',
        requireAuth: formData.requireAuth === true,
        
        // SEO
        seoTitle: formData.seoTitle || '',
        seoDescription: formData.seoDescription || '',
        seoKeywords: formData.seoKeywords || '',
        ogImage: formData.ogImage || '',
        customDomain: formData.customDomain || '',
        customPath: formData.customPath || '',
        enableStructuredData: formData.enableStructuredData !== false,
        allowIndexing: formData.allowIndexing !== false,
        
        // Styling
        primaryColor: formData.primaryColor || '',
        secondaryColor: formData.secondaryColor || '',
        backgroundColor: formData.backgroundColor || '',
        textColor: formData.textColor || '',
        headerColor: formData.headerColor || '',
        headerTextColor: formData.headerTextColor || '',
        fontFamily: formData.fontFamily || '',
        borderRadius: formData.borderRadius || '',
        
        // Header
        headerBackgroundUrl: formData.headerBackgroundUrl || '',
        headerOverlay: formData.headerOverlay || 'rgba(0, 0, 0, 0.3)',
        headerOverlayOpacity: formData.headerOverlayOpacity !== undefined ? formData.headerOverlayOpacity : 0.3,
        
        // Layout
        layoutStyle: formData.layoutStyle || '',
        enableParallax: formData.enableParallax !== false,
        enableAnimations: formData.enableAnimations !== false,
        
        // Interactions
        enableComments: formData.enableComments !== false,
        enableLikes: formData.enableLikes !== false,
        enableSharing: formData.enableSharing !== false,
        enableDownloads: formData.enableDownloads === true,
        
        // Advanced
        customCSS: formData.customCSS || '',
        customJS: formData.customJS || '',
        analyticsId: formData.analyticsId || '',
        
        // Institution settings - rebuild nested object
        institutionSettings: {

          ...(book?.institutionSettings || {}),

          academicYear: formData.academicYear || '',
          gradeRange: formData.gradeRange || '',
          admissionProcess: formData.admissionProcess || '',

          headerSettings: formData.headerSettings,

          viewSettings: formData.viewSettings,
          permissions: formData.permissions,
          authorizedEmails: formData.authorizedEmails,

          enableInternalCommunications: formData.enableInternalCommunications !== false,
          enableResourceManagement: formData.enableResourceManagement !== false,
          enableLearnerManagement: formData.enableLearnerManagement !== false,
          enablePerformanceTracking: formData.enablePerformanceTracking !== false,
          emailNotifications: formData.emailNotifications !== false,
          smsNotifications: formData.smsNotifications === true,
          pushNotifications: formData.pushNotifications !== false,
          sectionVisibility: formData.sectionVisibility || {
            programs: true,
            admissions: true,
            staff: true,
            facilities: true,
            events: true,
            news: true,
            gallery: true
          }
        },
        
        // Metadata
        templateType: 'institution',
        updatedAt: new Date()
      };

      // Merge with existing book data
      const updatedBook = {
        ...book,
        ...updateData
      };

      await firestoreService.updateBook(book.id, updatedBook);
      await onUpdate(updatedBook);
      setHasChanges(false);
      
    } catch (error) {
      console.error('Error saving institution settings:', error);
      
      let errorMessage = 'Failed to save settings. Please try again.';
      if (error.message.includes('permission')) {
        errorMessage = 'You do not have permission to update this institution.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // Keep your existing export/import handlers - NO CHANGES
  const handleExport = () => {
    const exportData = {
      institutionSettings: formData,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.botName || 'institution'}-settings.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        if (importedData.institutionSettings) {
          setFormData(prev => ({
            ...prev,
            ...importedData.institutionSettings
          }));
          setHasChanges(true);
        }
      } catch (error) {
        console.error('Error importing settings:', error);
        setErrors({ general: 'Failed to import settings. Please check the file format.' });
      }
    };
    reader.readAsText(file);
  };

  return (
    <SettingsModal
      title="Institution Settings"
      subtitle="Configure your institution's information, programs, and features"
      isOpen={true}
      onClose={onClose}
      onSave={handleSave}
      onExport={handleExport}
      onImport={handleImport}
      saving={saving}
      hasChanges={hasChanges}
      size="max-w-7xl"
    >
      {errors.general && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{errors.general}</span>
        </div>
      )}
      
      {/* FIXED: Proper flex structure for scrolling */}
      <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto shrink-0">
        <div className="p-4">
        <TabNavigation 
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasChanges={hasChanges}
        />
        </div>
      </div>
        
        {/* CRITICAL FIX: This wrapper enables scrolling */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6">
            {activeTab === 'basic' && (
              <BasicInfoTab
                formData={formData}
                onInputChange={handleInputChange}
                onImageUpload={handleImageUpload}
                templateType="institution"
                uploadStates={uploadStates}
                errors={errors}
              />
            )}

            {activeTab === 'header' && (
              <HeaderSettings
                formData={formData}
                onInputChange={handleInputChange}
                uploadStates={uploadStates}             
                onImageUpload={handleImageUpload}
              />
            )}

            {activeTab === 'view' && (
              <ViewSettings
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
              />
            )}

            {activeTab === 'permissions' && (
              <PermissionsSettings 
                bookId={bookId}
                bookData={book}
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
                currentUserRole="owner"
              />
            )}
            
            {activeTab === 'programs' && (
              <InstitutionProgramsTab
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            
            {activeTab === 'admissions' && (
              <InstitutionAdmissionsTab
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            
            {activeTab === 'staff' && (
              <InstitutionStaffTab
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            
            {activeTab === 'facilities' && (
              <InstitutionFacilitiesTab
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            {/*
            {activeTab === 'notifications' && (
              <InstitutionNotificationsTab
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            */}
            {activeTab === 'design' && (
              <DesignBrandingSection
                formData={formData}
                onInputChange={handleInputChange}
                onImageUpload={handleImageUpload}
                uploadStates={uploadStates}
                errors={errors}
              />
            )}
            
            {activeTab === 'layout' && (
              <LayoutDisplaySection
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            
            {activeTab === 'seo' && (
              <SEOSection
                formData={formData}
                onInputChange={handleInputChange}
                onImageUpload={handleImageUpload}
                uploadStates={uploadStates}
                errors={errors}
                getPreviewUrl={() => {
                  if (formData.customDomain) {
                    return formData.customPath ? 
                      `https://${formData.customDomain}/${formData.customPath}` :
                      `https://${formData.customDomain}`;
                  }
                  return formData.customPath ? 
                    `https://app.parrotbook.com/${formData.customPath}` :
                    `https://app.parrotbook.com/book/${book?.id}`;
                }}
              />
            )}
            
            {activeTab === 'integrations' && (
              <IntegrationsSection
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            
            {activeTab === 'interactions' && (
              <InteractionsSection
                formData={formData}
                onInputChange={handleInputChange}
                errors={errors}
              />
            )}
            
            {activeTab === 'privacy' && (
              <PrivacySection
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
              />
            )}
            
            {activeTab === 'advanced' && (
              <AdvancedSection
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
              />
            )}
          </div>
        </div>
    </SettingsModal>
  );
};

// Keep all your existing tab components exactly the same - NO CHANGES
const InstitutionProgramsTab = ({ formData, onInputChange, errors }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold">Academic Programs</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Academic Year
        </label>
        <input
          type="text"
          value={formData.institutionSettings?.academicYear || ''}
          onChange={(e) => onInputChange('institutionSettings.academicYear', e.target.value)}
          placeholder="e.g., 2024-2025"
          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Grade Range
        </label>
        <input
          type="text"
          value={formData.institutionSettings?.gradeRange || ''}
          onChange={(e) => onInputChange('institutionSettings.gradeRange', e.target.value)}
          placeholder="e.g., K-12, 9-12, Undergraduate"
          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.entries(formData.institutionSettings?.sectionVisibility || {}).map(([key, value]) => (
        <div key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
          <span className="text-sm font-medium text-gray-700 capitalize">
            Show {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
          </span>
          <button
            onClick={() => onInputChange('institutionSettings.sectionVisibility', {
              ...formData.institutionSettings.sectionVisibility,
              [key]: !value
            })}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              value ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                value ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      ))}
    </div>
  </div>
);

const InstitutionStaffTab = ({ formData, onInputChange, errors }) => (
  <div className="space-y-6 flex-1 p-3 overflow-y-auto">
    <h3 className="text-lg font-semibold">Staff & Leadership Settings</h3>
    <p className="text-gray-600">Configure staff management and communication features</p>
    
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
        <div>
          <h4 className="text-sm font-medium text-gray-700">Internal Communications</h4>
          <p className="text-xs text-gray-500">Enable staff messaging and announcements</p>
        </div>
        <button
          onClick={() => onInputChange('institutionSettings.enableInternalCommunications', !formData.institutionSettings?.enableInternalCommunications)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            formData.institutionSettings?.enableInternalCommunications ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              formData.institutionSettings?.enableInternalCommunications ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  </div>
);

const InstitutionFacilitiesTab = ({ formData, onInputChange, errors }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold">Facilities Management</h3>
    <p className="text-gray-600">Configure facility booking and resource management</p>
    
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Resource Management</h4>
        <p className="text-xs text-gray-500">Enable facility booking and resource allocation</p>
      </div>
      <button
        onClick={() => onInputChange('institutionSettings.enableResourceManagement', !formData.institutionSettings?.enableResourceManagement)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          formData.institutionSettings?.enableResourceManagement ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            formData.institutionSettings?.enableResourceManagement ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  </div>
);

const InstitutionAdmissionsTab = ({ formData, onInputChange, errors }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold">Admissions Settings</h3>
    
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Admission Process Description
      </label>
      <textarea
        value={formData.institutionSettings?.admissionProcess || ''}
        onChange={(e) => onInputChange('institutionSettings.admissionProcess', e.target.value)}
        placeholder="Describe your admission process..."
        rows={4}
        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>

    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Learner Management</h4>
        <p className="text-xs text-gray-500">Enable student tracking and management</p>
      </div>
      <button
        onClick={() => onInputChange('institutionSettings.enableLearnerManagement', !formData.institutionSettings?.enableLearnerManagement)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          formData.institutionSettings?.enableLearnerManagement ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            formData.institutionSettings?.enableLearnerManagement ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  </div>
);